/**
 * cellRules.js — pure predicates for the cell panel's edit gating.
 *
 * `cell_workflow`, `workspace`, and `workspace_mode` are STRUCTURAL choices: once the orchestrator has
 * started acting on them (the cell reached "working" at least once), changing them out from under an
 * in-flight run would desync the executing workflow from what the panel shows. They freeze at that
 * point; title/description stay editable per their own (unchanged) draft-only rule — see cellPanel.js.
 */

/**
 * Has this cell ever started running? True once the orchestrator has recorded a current step, or once
 * the status has reached (or passed through) "working" — covers a cell paused/failed/completed mid-run
 * as well as one currently in flight.
 * @param {{ props?: { workflow_state?: { current_step?: string }, status?: string } }} cell
 * @returns {boolean}
 */
export function cellHasRun(cell) {
  const p = (cell && cell.props) || {};
  return !!(p.workflow_state && p.workflow_state.current_step) ||
    ['working', 'input_needed', 'inactive', 'completed', 'failed'].includes(p.status);
}

/**
 * Whether the cell's structural props (cell_workflow / workspace / workspace_mode) are still editable —
 * true through draft/planned/ready, false from first "working" onward. The exact negation of
 * cellHasRun; kept as its own export so call sites read as intent ("is this editable?") rather than a
 * double negative.
 * @param {{ props?: { workflow_state?: { current_step?: string }, status?: string } }} cell
 * @returns {boolean}
 */
export function isStructureEditable(cell) {
  return !cellHasRun(cell);
}
